import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/fda";

const FUNNEL_STAGES = [
  ["ASSESSMENT_STARTED", "Assessment started"],
  ...Array.from({ length: 10 }, (_, i) => [`QUESTION_${i + 1}_COMPLETED`, `Question ${i + 1}`]),
  ["QUESTION_11_COMPLETED", "Contact details"],
  ["LEAD_SUBMITTED", "Lead submitted"],
];

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a) return <p className="text-sm text-forest-soft">Loading analytics…</p>;

  const d = a.dashboard;
  const started = a.funnel_events.ASSESSMENT_STARTED || 0;

  return (
    <div data-testid="admin-analytics-page">
      <h1 className="font-display text-3xl font-extrabold text-forest">Analytics</h1>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[["Assessment starts", started], ["Lead submissions", a.funnel_events.LEAD_SUBMITTED || 0],
          ["Contacted", d.contacted], ["Prospects", d.prospects], ["Referred", d.referred],
          ["Sales", d.sales], ["Unanswered", d.unanswered],
          ["Completion rate", `${started ? Math.round(((a.funnel_events.LEAD_SUBMITTED || 0) / started) * 100) : 0}%`]].map(([label, v]) => (
          <div key={label} className="rounded-2xl border border-border bg-white p-5" data-testid={`analytics-${label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-forest-soft">{label}</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-forest">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[["Lead → Contact", d.lead_to_contact], ["Lead → Prospect", d.lead_to_prospect],
          ["Prospect → Sale", d.prospect_to_sale], ["Lead → Sale", d.lead_to_sale]].map(([label, v]) => (
          <div key={label} className="rounded-2xl bg-forest p-5 text-sand">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-sand/60">{label}</p>
            <p className="mt-2 font-display text-2xl font-extrabold">{v}%</p>
          </div>
        ))}
      </div>

      <h2 className="mt-12 font-display text-xl font-bold text-forest">Leads by day</h2>
      <div className="mt-4 h-72 rounded-2xl border border-border bg-white p-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={a.by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(38 20% 88%)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="hsl(158 42% 24%)" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <h2 className="mt-12 font-display text-xl font-bold text-forest">Funnel drop-off</h2>
      <div className="mt-4 space-y-2 rounded-2xl border border-border bg-white p-5" data-testid="funnel-dropoff">
        {FUNNEL_STAGES.map(([k, label]) => {
          const v = a.funnel_events[k] || 0;
          const pct = started ? Math.round((v / started) * 100) : 0;
          return (
            <div key={k} className="flex items-center gap-4">
              <span className="w-40 shrink-0 text-xs font-semibold text-forest-soft">{label}</span>
              <div className="h-4 flex-1 overflow-hidden rounded-full bg-sand-deep">
                <div className="h-full rounded-full bg-terracotta" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-20 text-right text-xs font-bold text-forest">{v} ({pct}%)</span>
            </div>
          );
        })}
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        {[["Leads by source", a.by_source], ["Leads by campaign", a.by_campaign]].map(([title, rows]) => (
          <div key={title} className="rounded-2xl border border-border bg-white p-5">
            <h2 className="font-display text-lg font-bold text-forest">{title}</h2>
            <table className="mt-3 w-full text-left text-sm">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-border">
                    <td className="py-2 text-forest-soft">{r.key}</td>
                    <td className="py-2 text-right font-bold text-forest">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
